import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { FileProtectOutlined, MedicineBoxOutlined } from "@ant-design/icons";
import SummaryCard, { SummaryItem } from "../PeopleInsights/SummaryCard";
import "../PeopleInsights/PeopleInsights.scss";

type ExpiringItem = {
  userId: string;
  firstName: string;
  lastName: string;
  avatar?: { url?: string };
  position?: any;
  type: "contract" | "sanitary";
  dueDate: string | Date;
  daysUntil: number;
};

type Props = {
  items?: ExpiringItem[];
};

const ExpiringCredentials: React.FC<Props> = ({ items }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formatSecondary = (dueDate: string | Date, daysUntil: number): string => {
    const dateStr = dayjs(dueDate).format("DD MMM");
    if (daysUntil === 0) return `${dateStr} · ${t("Today")}`;
    if (daysUntil > 0) return `${dateStr} · ${t("in_days_short", { count: daysUntil })}`;
    return `${dateStr} · ${t("expired_days_short", { count: Math.abs(daysUntil) })}`;
  };

  const toSummaryItem = (item: ExpiringItem): SummaryItem => ({
    firstName: item.firstName,
    lastName: item.lastName,
    secondary: formatSecondary(item.dueDate, item.daysUntil),
    urgency:
      item.daysUntil < 0
        ? "expired"
        : item.daysUntil <= 7
          ? "urgent"
          : "normal",
    onClick: () => navigate(`/users/${item.userId}`),
  });

  const contracts = (items ?? [])
    .filter((i) => i.type === "contract")
    .map(toSummaryItem);

  const sanitary = (items ?? [])
    .filter((i) => i.type === "sanitary")
    .map(toSummaryItem);

  return (
    <div className="pi-grid">
      <SummaryCard
        icon={<FileProtectOutlined />}
        title={t("Expiring Contracts")}
        subtitle={t("Expiring soon")}
        count={contracts.length}
        accentColor="#dc2626"
        accentBg="#fef2f2"
        avatarColor="#991b1b"
        avatarBg="#fecaca"
        items={contracts}
        emptyText={t("No expiring contracts")}
      />
      <SummaryCard
        icon={<MedicineBoxOutlined />}
        title={t("Expiring Sanitary Booklets")}
        subtitle={t("Expiring soon")}
        count={sanitary.length}
        accentColor="#2563eb"
        accentBg="#eff6ff"
        avatarColor="#1e40af"
        avatarBg="#bfdbfe"
        items={sanitary}
        emptyText={t("No expiring sanitary booklets")}
      />
    </div>
  );
};

export default ExpiringCredentials;
